<template>
  <v-container class="audit-page py-4 px-4 px-md-6" fluid>
    <section class="audit-hero mb-3">
      <v-row align="center" no-gutters>
        <v-col cols="12" md="8">
          <div class="hero-kicker">
            <v-icon small color="primary" class="mr-1">mdi-clipboard-check-outline</v-icon>
            Calidad / Auditoría
          </div>
          <h1 class="hero-title">Encuesta de satisfacción</h1>
          <p class="hero-subtitle mb-0">
            Valida cuándo aplica la encuesta por ciclo y consulta el avance de respuestas en INBI, FAST y teachers.
          </p>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right mt-4 mt-md-0">
          <div class="hero-actions">
            <v-chip small class="hero-chip mb-2">
              <v-icon left x-small>mdi-calendar-check-outline</v-icon>
              {{ ciclo ? ciclo.ciclo : 'Sin ciclo' }}
            </v-chip>
            <v-btn
              rounded
              depressed
              color="white"
              class="hero-refresh"
              :loading="cargar"
              :disabled="!ciclo"
              @click="consultar"
            >
              <v-icon left small color="primary">mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </section>

    <v-card class="audit-panel mb-4" flat>
      <v-tabs
        v-model="tabEscuela"
        class="school-tabs"
        background-color="transparent"
        color="primary"
        slider-size="4"
      >
        <v-tab>
          <v-icon left small>mdi-school-outline</v-icon>
          INBI
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-certificate-outline</v-icon>
          FAST
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-human-male-board</v-icon>
          Teachers
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-row dense class="filter-grid">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="ciclo"
              :items="ciclosFiltrados"
              item-text="ciclo"
              return-object
              clearable
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-calendar-range"
              label="Ciclo"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="filtroPlantel"
              :items="plantelesEscuela"
              item-text="plantel"
              item-value="id_plantel"
              clearable
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-store-marker-outline"
              label="Sucursal"
              :disabled="!ciclo"
            ></v-autocomplete>
          </v-col>
          <v-col v-if="esTabTeachers" cols="12" md="4">
            <v-select
              v-model="opEscuelaTeachers"
              :items="opcionesEscuelaTeachers"
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-filter-outline"
              label="Escuela"
              hide-details
              :disabled="!ciclo"
            ></v-select>
          </v-col>
          <v-col cols="12" :md="esTabTeachers ? 4 : 4">
            <v-text-field
              v-model="search"
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-magnify"
              :label="esTabTeachers ? 'Buscar teacher o sucursal' : 'Buscar sucursal'"
              :disabled="esTabTeachers ? !teachersSeguimiento.length : !avancePorSucursal.length"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-card v-if="ventanaSeleccionada" class="ventana-card mb-4" flat>
          <v-card-text class="pa-4">
            <v-row align="center">
              <v-col cols="12" md="8">
                <div class="ventana-label">Ventana de aplicación</div>
                <div class="ventana-fechas">
                  {{ formatearFechaCorta(ventanaSeleccionada.fecha_inicio) }}
                  —
                  {{ formatearFechaCorta(ventanaSeleccionada.fecha_fin) }}
                </div>
                <div class="ventana-hint">
                  Último viernes, sábado y domingo del ciclo · Semana {{ ventanaSeleccionada.semana_aplicacion || 4 }}
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-md-right">
                <v-chip :color="estadoVentana.color" dark class="status-chip">
                  <v-icon left small>{{ estadoVentana.icon }}</v-icon>
                  {{ estadoVentana.label }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div v-if="!esTabTeachers" class="stats-strip mb-3">
          <div class="metric-pill">
            <v-icon small color="primary">mdi-account-group-outline</v-icon>
            <span>Total alumnos</span>
            <strong>{{ resumen.total_alumnos }}</strong>
          </div>
          <div class="metric-pill metric-exported">
            <v-icon small color="deep-purple darken-3">mdi-clipboard-check-outline</v-icon>
            <span>Realizadas</span>
            <strong>{{ resumen.alumnos_encuestados }}</strong>
          </div>
          <div class="metric-pill metric-pending">
            <v-icon small color="red darken-2">mdi-clock-outline</v-icon>
            <span>Pendientes</span>
            <strong>{{ totalPendientes }}</strong>
          </div>
          <div class="metric-pill metric-selected">
            <v-icon small color="teal darken-2">mdi-chart-donut</v-icon>
            <span>Participación</span>
            <strong>{{ porcentajeParticipacion }}%</strong>
          </div>
        </div>

        <div v-else class="stats-strip mb-3">
          <div class="metric-pill">
            <v-icon small color="primary">mdi-human-male-board</v-icon>
            <span>Total teachers</span>
            <strong>{{ resumenTeachers.total_empleados }}</strong>
          </div>
          <div class="metric-pill metric-exported">
            <v-icon small color="deep-purple darken-3">mdi-clipboard-check-outline</v-icon>
            <span>Contestaron</span>
            <strong>{{ resumenTeachers.empleados_encuestados }}</strong>
          </div>
          <div class="metric-pill metric-pending">
            <v-icon small color="red darken-2">mdi-clock-outline</v-icon>
            <span>Pendientes</span>
            <strong>{{ totalPendientesTeachers }}</strong>
          </div>
          <div class="metric-pill metric-selected">
            <v-icon small color="teal darken-2">mdi-chart-donut</v-icon>
            <span>Participación</span>
            <strong>{{ porcentajeParticipacionTeachers }}%</strong>
          </div>
        </div>

        <v-row v-if="esTabTeachers" dense class="mb-3">
          <v-col cols="12" sm="4">
            <v-card class="escuela-resumen-card" flat>
              <v-card-text class="py-3">
                <div class="escuela-resumen-titulo">INBI</div>
                <div class="escuela-resumen-linea">Contestaron: {{ resumenInbiTeachers.contestaron }} / {{ resumenInbiTeachers.total }}</div>
                <div class="escuela-resumen-linea">Avance: {{ resumenInbiTeachers.avance }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="escuela-resumen-card" flat>
              <v-card-text class="py-3">
                <div class="escuela-resumen-titulo">FAST</div>
                <div class="escuela-resumen-linea">Contestaron: {{ resumenFastTeachers.contestaron }} / {{ resumenFastTeachers.total }}</div>
                <div class="escuela-resumen-linea">Avance: {{ resumenFastTeachers.avance }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="escuela-resumen-card escuela-resumen-card--todo" flat>
              <v-card-text class="py-3">
                <div class="escuela-resumen-titulo">Total</div>
                <div class="escuela-resumen-linea">Contestaron: {{ resumenTodoTeachers.contestaron }} / {{ resumenTodoTeachers.total }}</div>
                <div class="escuela-resumen-linea">Avance: {{ resumenTodoTeachers.avance }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert dense text color="info" class="modern-alert mb-4">
          <v-icon left small color="info">mdi-information-outline</v-icon>
          <template v-if="!esTabTeachers">
            La encuesta solo bloquea grupos en la app durante la ventana del ciclo. Fuera de esas fechas el alumno entra con normalidad.
          </template>
          <template v-else>
            Encuesta de opinión docente (ambiente laboral). Misma ventana del ciclo: último viernes, sábado y domingo.
          </template>
        </v-alert>

        <v-expansion-panels class="template-panel mb-4" flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <div class="panel-title">
                  <v-icon color="primary" class="mr-2">mdi-calendar-month-outline</v-icon>
                  Calendario de ventanas por ciclo
                </div>
                <div class="panel-subtitle">Calendario oficial Excel (ciclos 06 al 12). Solo consulta.</div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :headers="headersCalendario"
                :items="calendarioCiclos"
                dense
                class="audit-table elevation-0"
                :items-per-page="7"
                hide-default-footer
              >
                <template v-slot:item.ventana="{ item }">
                  {{ item.ventanaTexto }}
                </template>
                <template v-slot:item.estado="{ item }">
                  <v-chip x-small :color="item.estado.color" dark>{{ item.estado.label }}</v-chip>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="!esTabTeachers" class="table-shell">
          <div class="table-header">
            <div>
              <span class="table-title">Avance por sucursal</span>
              <div class="table-subtitle">{{ escuelaTexto }} · {{ ciclo ? ciclo.ciclo : 'Selecciona un ciclo' }}</div>
            </div>
            <v-chip small color="primary" outlined>
              {{ avanceFiltrado.length }} sucursales
            </v-chip>
          </div>
          <v-data-table
            :headers="headersAvance"
            :items="avanceFiltrado"
            :search="search"
            dense
            :loading="cargar"
            class="audit-table elevation-0"
            :items-per-page="15"
          >
            <template v-slot:item.avance="{ item }">
              <v-chip x-small :color="colorAvance(item.avance)" dark>{{ item.avance }}%</v-chip>
            </template>
            <template v-slot:no-data>
              <div class="py-6 text-center text--secondary">
                Selecciona un ciclo para ver el avance de encuestas.
              </div>
            </template>
          </v-data-table>
        </div>

        <div v-else class="table-shell mb-4">
          <div class="table-header">
            <div>
              <span class="table-title">Avance por sucursal</span>
              <div class="table-subtitle">{{ escuelaTeachersTexto }} · {{ ciclo ? ciclo.ciclo : 'Selecciona un ciclo' }}</div>
            </div>
            <v-chip small color="primary" outlined>
              {{ avanceTeachersPorSucursal.length }} sucursales
            </v-chip>
          </div>
          <v-data-table
            :headers="headersAvance"
            :items="avanceTeachersPorSucursal"
            :search="search"
            dense
            :loading="cargar"
            class="audit-table elevation-0"
            :items-per-page="15"
          >
            <template v-slot:item.avance="{ item }">
              <v-chip x-small :color="colorAvance(item.avance)" dark>{{ item.avance }}%</v-chip>
            </template>
            <template v-slot:no-data>
              <div class="py-6 text-center text--secondary">
                Selecciona un ciclo para ver el avance de teachers.
              </div>
            </template>
          </v-data-table>
        </div>

        <div v-if="esTabTeachers" class="table-shell">
          <div class="table-header">
            <div>
              <span class="table-title">Seguimiento de teachers</span>
              <div class="table-subtitle">{{ escuelaTeachersTexto }} · {{ ciclo ? ciclo.ciclo : 'Selecciona un ciclo' }}</div>
            </div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <v-select
                v-model="filtroEstatusTeachers"
                :items="opcionesEstatusTeachers"
                dense
                outlined
                rounded
                hide-details
                label="Estatus"
                class="filtro-estatus-teachers"
                style="max-width: 180px;"
              ></v-select>
              <v-chip small color="primary" outlined>
                {{ teachersSeguimientoFiltrados.length }} teachers
              </v-chip>
            </div>
          </div>
          <v-data-table
            :headers="headersTeachersSeguimiento"
            :items="teachersSeguimientoFiltrados"
            :search="search"
            dense
            :loading="cargar"
            class="audit-table elevation-0"
            :items-per-page="15"
          >
            <template v-slot:item.estatus="{ item }">
              <v-chip
                x-small
                :color="item.estatus === 'Contestó' ? 'green darken-1' : 'orange darken-2'"
                dark
              >{{ item.estatus }}</v-chip>
            </template>
            <template v-slot:item.fecha_encuesta="{ item }">
              {{ formatearFechaEncuesta(item.fecha_encuesta) }}
            </template>
            <template v-slot:no-data>
              <div class="py-6 text-center text--secondary">
                Selecciona un ciclo para ver el seguimiento de teachers.
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'
import {
  buildVentanaEncuestaSatisfaccion,
  enVentanaSatisfaccion,
  idCicloSugeridoEncuestaSatisfaccion,
  hoyYmdSatisfaccion,
  ORDEN_CALENDARIO_EXCEL,
  VENTANAS_EXCEL_CICLOS,
  claveCicloDesdeNombre,
} from '@/helpers/encuesta-satisfaccion-ventana.js'

export default {
  name: 'AuditoriaEncuestaSatisfaccion',

  components: { Alerta, carga },

  mixins: [validarErrores],

  data: () => ({
    tabEscuela: 0,
    ciclo: null,
    ciclos: [],
    filtroPlantel: null,
    planteles: [],
    search: '',
    cargar: false,
    respuestaAlerta: false,
    parametros: { dialogError: false, mensaje: '', color: '' },
    resumen: {
      total_alumnos: 0,
      alumnos_encuestados: 0,
      porcentaje_participacion: '0.00',
    },
    avancePorSucursal: [],
    faltantes: [],
    opEscuelaTeachers: 0,
    opcionesEscuelaTeachers: [
      { text: 'Todo', value: 0 },
      { text: 'INBI', value: 1 },
      { text: 'FAST', value: 2 },
    ],
    resumenTeachers: {
      total_empleados: 0,
      empleados_encuestados: 0,
      porcentaje_participacion: '0.00',
    },
    resumenFaltantesEscuela: {
      inbi: { total: 0, faltan: 0, avance: '0.00' },
      fast: { total: 0, faltan: 0, avance: '0.00' },
      todo: { total: 0, faltan: 0, avance: '0.00' },
    },
    teachersSeguimiento: [],
    filtroEstatusTeachers: null,
    opcionesEstatusTeachers: [
      { text: 'Todos', value: null },
      { text: 'Contestó', value: 'Contestó' },
      { text: 'Pendiente', value: 'Pendiente' },
    ],
    headersTeachersSeguimiento: [
      { text: 'Escuela', value: 'escuela' },
      { text: 'Nombre', value: 'nombre_completo' },
      { text: 'Estatus', value: 'estatus' },
      { text: 'Fecha encuesta', value: 'fecha_encuesta' },
      { text: 'Sucursal', value: 'plantel' },
      { text: 'Puesto', value: 'puesto' },
      { text: 'Departamento', value: 'departamento' },
    ],
    headersCalendario: [
      { text: 'Ciclo', value: 'ciclo' },
      { text: 'Inicio ciclo', value: 'inicio' },
      { text: 'Fin ciclo', value: 'fin' },
      { text: 'Ventana encuesta', value: 'ventana', sortable: false },
      { text: 'Semana', value: 'semana', align: 'center' },
      { text: 'Reporte', value: 'reporte' },
      { text: 'Fecha junta', value: 'junta' },
      { text: 'Estado', value: 'estado', sortable: false },
    ],
    headersAvance: [
      { text: 'Sucursal', value: 'plantel' },
      { text: 'Total', value: 'total', align: 'end' },
      { text: 'Encuestados', value: 'encuestados', align: 'end' },
      { text: 'Faltan', value: 'faltan', align: 'end' },
      { text: 'Avance (%)', value: 'avance', align: 'end' },
    ],
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    esTabTeachers () {
      return this.tabEscuela === 2
    },

    escuela () {
      if (this.esTabTeachers) return this.opEscuelaTeachers
      return this.tabEscuela === 0 ? 1 : 2
    },

    escuelaTexto () {
      if (this.esTabTeachers) return this.escuelaTeachersTexto
      return this.escuela === 1 ? 'INBI' : 'FAST'
    },

    escuelaTeachersTexto () {
      if (this.opEscuelaTeachers === 1) return 'INBI'
      if (this.opEscuelaTeachers === 2) return 'FAST'
      return 'Todo'
    },

    ciclosFiltrados () {
      if (this.esTabTeachers) {
        return this.ciclos.filter((c) => {
          const nombre = String(c.ciclo || '')
          if (/INVER|CAMBIOS|EXCI/i.test(nombre)) return false
          return !nombre.includes('FE')
        })
      }
      const esFast = this.escuela === 2
      return this.ciclos.filter((c) => {
        const nombre = String(c.ciclo || '')
        if (/INVER|CAMBIOS|EXCI/i.test(nombre)) return false
        const tieneFe = nombre.includes('FE')
        return esFast ? tieneFe : !tieneFe
      })
    },

    ventanaSeleccionada () {
      if (!this.ciclo) return null
      return buildVentanaEncuestaSatisfaccion(this.ciclo)
    },

    estadoVentana () {
      if (!this.ventanaSeleccionada) {
        return { label: 'Sin ventana', color: 'grey', icon: 'mdi-help-circle-outline' }
      }
      if (enVentanaSatisfaccion(this.ventanaSeleccionada)) {
        return { label: 'En ventana', color: 'green darken-1', icon: 'mdi-calendar-clock' }
      }
      const hoy = hoyYmdSatisfaccion()
      if (hoy < this.ventanaSeleccionada.fecha_inicio) {
        return { label: 'Próxima', color: 'amber darken-2', icon: 'mdi-calendar-arrow-right' }
      }
      return { label: 'Finalizada', color: 'blue-grey', icon: 'mdi-calendar-check' }
    },

    totalPendientes () {
      return Math.max(Number(this.resumen.total_alumnos) - Number(this.resumen.alumnos_encuestados), 0)
    },

    porcentajeParticipacion () {
      const p = Number(this.resumen.porcentaje_participacion)
      return Number.isFinite(p) ? p.toFixed(1) : '0.0'
    },

    totalPendientesTeachers () {
      return Math.max(
        Number(this.resumenTeachers.total_empleados) - Number(this.resumenTeachers.empleados_encuestados),
        0
      )
    },

    porcentajeParticipacionTeachers () {
      const p = Number(this.resumenTeachers.porcentaje_participacion)
      return Number.isFinite(p) ? p.toFixed(1) : '0.0'
    },

    resumenInbiTeachers () {
      return this.resumenEscuelaTeachers('inbi')
    },

    resumenFastTeachers () {
      return this.resumenEscuelaTeachers('fast')
    },

    resumenTodoTeachers () {
      return this.resumenEscuelaTeachers('todo')
    },

    teachersSeguimientoActivos () {
      return this.filtrarTeachersPorEscuela(this.teachersSeguimiento)
    },

    teachersSeguimientoFiltrados () {
      let lista = this.teachersSeguimientoActivos
      if (this.filtroEstatusTeachers) {
        lista = lista.filter((t) => t.estatus === this.filtroEstatusTeachers)
      }
      return lista
    },

    avanceTeachersPorSucursal () {
      const mapa = new Map()
      this.teachersSeguimientoActivos.forEach((row) => {
        const id = Number(row.id_plantel)
        if (!Number.isFinite(id) || id <= 0) return
        if (!mapa.has(id)) {
          mapa.set(id, {
            id_plantel: id,
            plantel: row.plantel,
            total: 0,
            encuestados: 0,
            faltan: 0,
          })
        }
        const item = mapa.get(id)
        item.total += 1
        if (row.estatus === 'Contestó') item.encuestados += 1
        else item.faltan += 1
      })
      return [...mapa.values()]
        .map((row) => ({
          ...row,
          avance: row.total ? Number(((row.encuestados / row.total) * 100).toFixed(2)) : 0,
        }))
        .sort((a, b) => String(a.plantel).localeCompare(String(b.plantel)))
    },

    avanceFiltrado () {
      let lista = Array.isArray(this.avancePorSucursal) ? this.avancePorSucursal : []
      if (this.escuela === 2) {
        lista = lista.filter((row) => this.esPlantelFast(row.plantel))
      } else {
        lista = lista.filter((row) => !this.esPlantelFast(row.plantel))
      }
      return lista
    },

    plantelesEscuela () {
      const lista = Array.isArray(this.planteles) ? this.planteles : []
      if (this.esTabTeachers) {
        if (this.opEscuelaTeachers === 2) {
          return lista.filter((p) => this.esPlantelFast(p.plantel))
        }
        if (this.opEscuelaTeachers === 1) {
          return lista.filter((p) => !this.esPlantelFast(p.plantel))
        }
        return lista
      }
      if (this.escuela === 2) {
        return lista.filter((p) => this.esPlantelFast(p.plantel))
      }
      return lista.filter((p) => !this.esPlantelFast(p.plantel))
    },

    calendarioCiclos () {
      return ORDEN_CALENDARIO_EXCEL.map((clave) => {
        const ventana = VENTANAS_EXCEL_CICLOS[clave]
        const erp = this.ciclosFiltrados.find((c) => claveCicloDesdeNombre(c.ciclo) === clave)
        return {
          ciclo: erp ? erp.ciclo : `CICLO ${clave}`,
          inicio: erp ? this.formatearFechaCorta(erp.fecha_inicio_ciclo) : '—',
          fin: erp ? this.formatearFechaCorta(erp.fecha_fin_ciclo) : '—',
          ventanaTexto: `${this.formatearFechaCorta(ventana.fecha_inicio)} — ${this.formatearFechaCorta(ventana.fecha_fin)}`,
          semana: `Semana ${ventana.semana_aplicacion}`,
          reporte: this.formatearFechaCorta(ventana.fecha_reporte),
          junta: this.formatearFechaCorta(ventana.fecha_junta),
          estado: this.estadoVentanaDeCiclo(ventana),
        }
      })
    },
  },

  watch: {
    tabEscuela () {
      this.ciclo = null
      this.filtroPlantel = null
      this.search = ''
      this.filtroEstatusTeachers = null
      this.limpiarDatos()
      this.sugerirCiclo()
    },
    opEscuelaTeachers () {
      if (this.ciclo) this.consultar()
    },
    ciclo (val) {
      if (val) this.consultar()
      else this.limpiarDatos()
    },
    filtroPlantel () {
      if (this.ciclo) this.consultar()
    },
  },

  async created () {
    await this.getCiclos()
    this.sugerirCiclo()
  },

  methods: {
    limpiarDatos () {
      this.resumen = { total_alumnos: 0, alumnos_encuestados: 0, porcentaje_participacion: '0.00' }
      this.resumenTeachers = { total_empleados: 0, empleados_encuestados: 0, porcentaje_participacion: '0.00' }
      this.resumenFaltantesEscuela = {
        inbi: { total: 0, faltan: 0, avance: '0.00' },
        fast: { total: 0, faltan: 0, avance: '0.00' },
        todo: { total: 0, faltan: 0, avance: '0.00' },
      }
      this.avancePorSucursal = []
      this.faltantes = []
      this.teachersSeguimiento = []
      this.planteles = []
    },

    resumenEscuelaTeachers (clave) {
      const bloque = (this.resumenFaltantesEscuela && this.resumenFaltantesEscuela[clave]) || {}
      const total = Number(bloque.total) || 0
      const faltan = Number(bloque.faltan) || 0
      const contestaron = Math.max(total - faltan, 0)
      const avance = bloque.avance != null
        ? Number(bloque.avance).toFixed(1)
        : (total ? ((contestaron / total) * 100).toFixed(1) : '0.0')
      return { total, contestaron, avance }
    },

    filtrarTeachersPorEscuela (items) {
      let lista = Array.isArray(items) ? items : []
      if (this.opEscuelaTeachers === 1) {
        lista = lista.filter((t) => String(t.escuela || '').toUpperCase() === 'INBI')
      } else if (this.opEscuelaTeachers === 2) {
        lista = lista.filter((t) => String(t.escuela || '').toUpperCase() === 'FAST')
      }
      return lista
    },

    sugerirCiclo () {
      const id = idCicloSugeridoEncuestaSatisfaccion(this.ciclosFiltrados)
      if (id == null) return
      const encontrado = this.ciclosFiltrados.find((c) => Number(c.id_ciclo) === Number(id))
      if (encontrado) this.ciclo = encontrado
    },

    getCiclos () {
      this.cargar = true
      return this.$http.get('ciclos.activos.erp')
        .then((response) => {
          this.ciclos = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.message) || 'Error al cargar ciclos'
          this.validarError(msg)
        })
        .finally(() => { this.cargar = false })
    },

    idCicloEncuestaSeleccionado () {
      const c = this.ciclo
      if (!c) return null
      // FAST: id del ciclo FE seleccionado (igual que inscripciones/certificados)
      const id = Number(c.id_ciclo)
      return Number.isFinite(id) && id > 0 ? id : null
    },

    esPlantelFast (nombre) {
      return /FAST/i.test(String(nombre || ''))
    },

    consultar () {
      if (this.esTabTeachers) return this.consultarTeachers()
      const idCiclo = this.idCicloEncuestaSeleccionado()
      if (idCiclo == null) {
        this.validarError('Selecciona un ciclo válido.')
        return
      }
      this.cargar = true
      const payload = {
        id_ciclo: idCiclo,
        op_escuela: this.escuela,
        iderp: this.getdatosUsuarioERPERP.iderp,
        idpuesto: this.getdatosUsuarioERPERP.idpuesto,
        planteles: this.getdatosUsuarioERPERP.planteles,
        id_plantel: this.filtroPlantel || null,
      }
      return this.$http.post('encuestas.satisfaccion.resultados', payload)
        .then((response) => {
          const d = response.data || {}
          this.faltantes = Array.isArray(d.faltantes) ? d.faltantes : []
          this.avancePorSucursal = Array.isArray(d.avancePorSucursal) ? d.avancePorSucursal : []
          const plantelesApi = Array.isArray(d.planteles) ? d.planteles : []
          this.planteles = plantelesApi.length
            ? plantelesApi
            : this.avancePorSucursal.map((row) => ({
              id_plantel: row.id_plantel,
              plantel: row.plantel,
            }))
          this.resumen = Object.assign(
            { total_alumnos: 0, alumnos_encuestados: 0, porcentaje_participacion: '0.00' },
            d.resumen || {}
          )
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.message) || 'Error al consultar encuesta'
          this.validarError(msg)
        })
        .finally(() => { this.cargar = false })
    },

    consultarTeachers () {
      const idCiclo = this.idCicloEncuestaSeleccionado()
      if (idCiclo == null) {
        this.validarError('Selecciona un ciclo válido.')
        return
      }
      this.cargar = true
      const payload = {
        id_ciclo: idCiclo,
        op_escuela: this.opEscuelaTeachers,
        iderp: this.getdatosUsuarioERPERP.iderp,
        idpuesto: this.getdatosUsuarioERPERP.idpuesto,
        id_plantel: this.filtroPlantel || null,
      }
      return this.$http.post('encuestas.ambiente.resultados', payload)
        .then((response) => {
          const d = response.data || {}
          this.teachersSeguimiento = Array.isArray(d.teachers_seguimiento) ? d.teachers_seguimiento : []
          this.planteles = Array.isArray(d.planteles) ? d.planteles : []
          this.resumenTeachers = Object.assign(
            { total_empleados: 0, empleados_encuestados: 0, porcentaje_participacion: '0.00' },
            d.resumen || {}
          )
          const resumenEscuela = d.resumen_faltantes_escuela || {}
          this.resumenFaltantesEscuela = {
            inbi: Object.assign({ total: 0, faltan: 0, avance: '0.00' }, resumenEscuela.inbi || {}),
            fast: Object.assign({ total: 0, faltan: 0, avance: '0.00' }, resumenEscuela.fast || {}),
            todo: Object.assign({ total: 0, faltan: 0, avance: '0.00' }, resumenEscuela.todo || {}),
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.message) || 'Error al consultar encuesta de teachers'
          this.validarError(msg)
        })
        .finally(() => { this.cargar = false })
    },

    formatearFechaEncuesta (fecha) {
      if (!fecha) return '—'
      const d = new Date(fecha)
      if (Number.isNaN(d.getTime())) return fecha
      return d.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },

    estadoVentanaDeCiclo (ventana) {
      if (!ventana) return { label: 'Sin datos', color: 'grey' }
      if (enVentanaSatisfaccion(ventana)) return { label: 'En ventana', color: 'green darken-1' }
      const hoy = hoyYmdSatisfaccion()
      if (hoy < ventana.fecha_inicio) return { label: 'Próxima', color: 'amber darken-2' }
      return { label: 'Finalizada', color: 'blue-grey' }
    },

    formatearFechaCorta (value) {
      const ymd = String(value || '').slice(0, 10)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '—'
      const [y, m, d] = ymd.split('-').map(Number)
      const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      return `${d} ${meses[m - 1]} ${y}`
    },

    colorAvance (avance) {
      const n = Number(avance)
      if (!Number.isFinite(n) || n <= 0) return 'red darken-2'
      if (n < 50) return 'orange darken-2'
      if (n < 80) return 'amber darken-2'
      return 'green darken-1'
    },
  },
}
</script>

<style scoped>
.audit-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 8% 5%, rgba(46, 218, 213, 0.22), transparent 22rem),
    radial-gradient(circle at 88% 18%, rgba(88, 70, 246, 0.16), transparent 24rem),
    linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.audit-hero,
.audit-panel,
.table-shell,
.template-panel .v-expansion-panel,
.ventana-card {
  border-radius: 24px !important;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 46px rgba(15, 23, 42, 0.08) !important;
}

.audit-hero {
  padding: 16px 22px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(245, 250, 255, 0.96)),
    linear-gradient(90deg, rgba(0, 13, 74, 0.05), rgba(46, 218, 213, 0.08));
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-title {
  margin: 4px 0 1px;
  font-size: clamp(1.32rem, 1.8vw, 1.7rem);
  font-weight: 800;
}

.hero-subtitle {
  max-width: 760px;
  color: #64748b;
  font-size: 0.8rem;
}

.hero-actions {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
}

.hero-chip {
  color: #475569 !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0;
}

.hero-refresh {
  font-weight: 700;
  text-transform: none;
  border: 1px solid #e2e8f0;
}

.audit-panel {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94) !important;
}

.school-tabs {
  padding: 6px 18px 0;
}

.school-tabs .v-tab {
  min-width: 140px;
  font-weight: 800;
  text-transform: none;
}

.stats-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.metric-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 6px 10px;
  border-radius: 999px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.07);
}

.metric-pill span {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.metric-pill strong {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 900;
}

.metric-pending { background: #fff7f7; }
.metric-exported { background: #f7f3ff; }
.metric-selected { background: #ecfeff; }

.ventana-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%) !important;
}

.ventana-label {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.06em;
}

.ventana-fechas {
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0;
}

.ventana-hint {
  font-size: 0.8rem;
  color: #64748b;
}

.modern-alert {
  border-radius: 18px;
  border: 1px solid rgba(3, 169, 244, 0.14);
}

.panel-title,
.table-title {
  display: flex;
  align-items: center;
  color: #0f172a;
  font-weight: 800;
}

.panel-subtitle,
.table-subtitle {
  font-size: 0.82rem;
  color: #64748b;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
}

.table-shell {
  overflow: hidden;
  background: white;
}

.audit-table ::v-deep thead th {
  color: #64748b !important;
  font-size: 0.74rem !important;
  font-weight: 800 !important;
}


.status-chip {
  font-weight: 700;
}

.escuela-resumen-card {
  border-radius: 16px !important;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #f8fbff;
}

.escuela-resumen-card--todo {
  background: #ecfeff;
}

.escuela-resumen-titulo {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.05em;
}

.escuela-resumen-linea {
  font-size: 0.85rem;
  color: #0f172a;
  margin-top: 2px;
}
</style>
